import * as jose from "jose";
import { DateTime, Duration, DurationLikeObject } from "luxon";
import { HEADER_KEYS } from "./headers";
import { handleJwtError } from "./token";

/**
 * Short-lived HMAC attestation that proves a GraphQL request originated from
 * the Next.js SSR layer and that the supplied client IP is the real visitor.
 *
 * Why this exists: SSR makes internal pod-to-pod calls to `/api/graphql`. Without
 * an attestation, the server cannot tell the difference between (a) trusted SSR
 * forwarding the visitor's IP via `x-real-ip`, and (b) an attacker on the public
 * endpoint spoofing the same header to bypass per-IP rate limits. The HMAC
 * proves (a). See ENG-6292 for the original bypass that this replaces.
 */

export const SSR_ATTESTATION_TTL: DurationLikeObject = { seconds: 60 };
const ATTESTATION_ALG = "HS256";

let cachedSecret: { raw: string | undefined; key: Uint8Array | null } | undefined;
const getSecret = (override?: string): Uint8Array | null => {
  if (override !== undefined) {
    return Buffer.from(override, "base64");
  }
  const raw = process.env.DREAM_SSR_HMAC_SECRET;
  if (!cachedSecret || cachedSecret.raw !== raw) {
    cachedSecret = { raw, key: raw ? Buffer.from(raw, "base64") : null };
  }
  return cachedSecret.key;
};

type SsrAttestationClaims = {
  ip: string;
};

/**
 * Sign an attestation for the given client IP. Returns null when the secret is
 * not configured so callers can degrade gracefully to the unsigned SSR path
 * (rather than crashing every page render).
 */
export const signSsrAttestation = async (
  claims: SsrAttestationClaims,
  secret?: string
): Promise<string | null> => {
  const key = getSecret(secret);
  if (!key) return null;
  return new jose.SignJWT(claims)
    .setProtectedHeader({ alg: ATTESTATION_ALG })
    .setIssuedAt()
    .setExpirationTime(
      Math.floor(DateTime.utc().plus(Duration.fromObject(SSR_ATTESTATION_TTL)).toSeconds())
    )
    .sign(key);
};

/**
 * Verify an attestation token. Returns the attested IP on success, or null on
 * any failure (missing secret, missing/invalid/expired/tampered token).
 * Never throws — every caller is expected to fall back to the unsigned IP
 * resolution path on null.
 */
export const verifySsrAttestation = async (
  token: string | undefined | null,
  secret?: string
): Promise<SsrAttestationClaims | null> => {
  if (!token) return null;
  const key = getSecret(secret);
  if (!key) return null;
  try {
    const { payload } = await jose.jwtVerify<SsrAttestationClaims>(token, key, {
      algorithms: [ATTESTATION_ALG],
    });
    if (typeof payload.ip !== "string" || !payload.ip) return null;
    return { ip: payload.ip };
  } catch (e) {
    handleJwtError(e);
    return null;
  }
};

type ResolvedSsrAttestation = {
  clientIp: string | null;
  attestation: string | null;
};

/**
 * Read the visitor's IP from the surrounding Next.js request scope and produce
 * a fresh attestation. Both SSR fetcher paths (Apollo's `ssrHttpLink` and the
 * server-actions fetcher) need this, so it lives here to keep the wiring
 * identical.
 *
 * Returns `{ clientIp: null, attestation: null }` when called outside a request
 * scope (module eval, static generation) — the caller forwards nothing and the
 * server falls back to its unsigned IP-resolution path.
 */
export const resolveSsrAttestation = async (): Promise<ResolvedSsrAttestation> => {
  try {
    const { headers: nextHeaders } = await import("next/headers");
    const reqHeaders = await nextHeaders();
    const clientIp =
      reqHeaders.get(HEADER_KEYS.CF_CONNECTING_IP) ||
      reqHeaders.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      null;
    if (!clientIp) return { clientIp: null, attestation: null };
    return { clientIp, attestation: await signSsrAttestation({ ip: clientIp }) };
  } catch {
    return { clientIp: null, attestation: null };
  }
};
